import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Search, DollarSign, UserPlus } from 'lucide-react';
import axios from 'axios';
import caseLabel from '../../utils/caseLabel';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function CreateInvoiceModal({
  isOpen,
  onClose,
  onInvoiceCreated,
  initialCaseId = null,
  initialContactId = null,
  initialContactName = null,
  initialLineItems = null
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Contact/Case search
  const [contacts, setContacts] = useState([]);
  const [cases, setCases] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [creatingContact, setCreatingContact] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    contact_id: '',
    contact_name: '',
    case_id: '',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    tax_rate: 0,
    discount_amount: 0,
    payment_terms: 'Net 30',
    notes: '',
    terms_conditions: ''
  });

  // Line items
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, time_entry_id: null, expense_id: null }
  ]);

  useEffect(() => {
    if (isOpen) {
      fetchContacts();

      // Pre-populate from initial props if provided
      if (initialContactId || initialCaseId || initialLineItems) {
        // Set form data with initial values
        if (initialContactId) {
          setFormData(prev => ({
            ...prev,
            contact_id: initialContactId,
            contact_name: initialContactName || '',
            case_id: initialCaseId || ''
          }));
          setContactSearch(initialContactName || '');
        }

        // Set initial line items if provided
        if (initialLineItems && initialLineItems.length > 0) {
          setLineItems(initialLineItems.map(item => ({
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            time_entry_id: item.time_entry_id || null,
            expense_id: item.expense_id || null
          })));
        }
      }
    }
  }, [isOpen, initialContactId, initialContactName, initialCaseId, initialLineItems]);

  useEffect(() => {
    if (formData.contact_id) {
      fetchCasesForContact(formData.contact_id);
    } else {
      setCases([]);
    }
  }, [formData.contact_id]);

  useEffect(() => {
    if (contactSearch.length > 0) {
      // First filter from cached contacts
      const filtered = contacts.filter(c =>
        c.contact_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.primary_email_address?.toLowerCase().includes(contactSearch.toLowerCase())
      );
      setFilteredContacts(filtered.slice(0, 10));
      setShowContactDropdown(true);
      
      // Also search the backend for better results
      const searchBackend = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/contacts/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { search: contactSearch, page_size: 20 }
          });
          const results = response.data.contacts || response.data || [];
          if (results.length > 0) {
            // Merge with existing filtered results, avoiding duplicates
            const existingIds = new Set(filtered.map(c => c.id));
            const newResults = results.filter(c => !existingIds.has(c.id));
            setFilteredContacts([...filtered.slice(0, 5), ...newResults].slice(0, 10));
          }
        } catch (err) {
          console.error('Error searching contacts:', err);
        }
      };
      
      // Debounce the backend search
      const timer = setTimeout(searchBackend, 300);
      return () => clearTimeout(timer);
    } else {
      setFilteredContacts([]);
      setShowContactDropdown(false);
    }
  }, [contactSearch, contacts]);

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/contacts/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page_size: 500 }
      });
      setContacts(response.data.contacts || response.data || []);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to load contacts. You can still search by typing a name.');
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchCasesForContact = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cases/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { contact_id: contactId, page_size: 100 }
      });
      setCases(response.data.cases || response.data || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setCases([]);
    }
  };


  const handleCreateContact = async () => {
    if (!newContact.first_name || !newContact.last_name) {
      setError("First and last name are required");
      return;
    }
    setCreatingContact(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/contacts/`, {
        first_name: newContact.first_name,
        last_name: newContact.last_name,
        primary_email_address: newContact.email || null,
        primary_phone_number: newContact.phone || null,
        contact_type: "individual"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const created = response.data;
      setContacts([created, ...contacts]);
      selectContact(created);
      setShowCreateContact(false);
      setNewContact({ first_name: "", last_name: "", email: "", phone: "" });
    } catch (err) {
      console.error("Error creating contact:", err);
      setError(err.response?.data?.detail || "Failed to create contact");
    } finally {
      setCreatingContact(false);
    }
  };

  const selectContact = (contact) => {
    setFormData({
      ...formData,
      contact_id: contact.id,
      contact_name: contact.contact_name
    });
    setContactSearch(contact.contact_name);
    setShowContactDropdown(false);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, time_entry_id: null, expense_id: null }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setLineItems(updated);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (formData.tax_rate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() - formData.discount_amount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.contact_id) {
      setError('Please select a contact');
      return;
    }

    if (lineItems.length === 0 || !lineItems.some(item => item.description && item.unit_price > 0)) {
      setError('Please add at least one line item with a description and price');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        contact_id: formData.contact_id,
        case_id: formData.case_id || null,
        due_date: new Date(formData.due_date).toISOString(),
        tax_rate: parseFloat(formData.tax_rate) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        payment_terms: formData.payment_terms,
        notes: formData.notes || null,
        terms_conditions: formData.terms_conditions || null,
        line_items: lineItems.filter(item => item.description).map((item, idx) => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          sort_order: idx,
          time_entry_id: item.time_entry_id || null,
          expense_id: item.expense_id || null
        }))
      };

      const response = await axios.post(`${API_URL}/invoices/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onInvoiceCreated(response.data);
      handleClose();
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      contact_id: '',
      contact_name: '',
      case_id: '',
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      tax_rate: 0,
      discount_amount: 0,
      payment_terms: 'Net 30',
      notes: '',
      terms_conditions: ''
    });
    setLineItems([{ description: '', quantity: 1, unit_price: 0, time_entry_id: null, expense_id: null }]);
    setContactSearch('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-virgo-dark/95 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-virgo-teal-500/30">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-virgo-teal-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-virgo-teal-500/20 rounded-lg">
              <DollarSign className="w-5 h-5 text-virgo-teal-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Create New Invoice</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-virgo-dark-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Contact Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Client / Contact *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  onFocus={() => setShowContactDropdown(true)}
                  placeholder={loadingContacts ? "Loading contacts..." : "Search contacts..."}
                  className="w-full pl-10 pr-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500"
                />
              </div>
              {showContactDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredContacts.map(contact => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => selectContact(contact)}
                      className="w-full px-4 py-2 text-left hover:bg-virgo-teal-500/20 text-white text-sm"
                    >
                      <div>{contact.contact_name}</div>
                      {contact.primary_email_address && (
                        <div className="text-xs text-gray-400">{contact.primary_email_address}</div>
                      )}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => { setShowContactDropdown(false); setShowCreateContact(true); }}
                    className="w-full px-4 py-2 text-left hover:bg-green-500/20 text-green-400 text-sm border-t border-virgo-teal-500/20 flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    Create New Contact
                  </button>
                </div>
              )}
              
              {/* Create Contact Inline Form */}
              {showCreateContact && (
                <div className="absolute z-10 w-full mt-1 bg-virgo-dark-800 border border-green-500/30 rounded-lg shadow-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-green-400 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" /> New Contact
                    </span>
                    <button type="button" onClick={() => setShowCreateContact(false)} className="text-gray-400 hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={newContact.first_name}
                      onChange={(e) => setNewContact({...newContact, first_name: e.target.value})}
                      className="px-3 py-2 bg-virgo-dark-700 border border-virgo-teal-500/30 rounded text-white text-sm placeholder-gray-500"
                    />
                    <input
                      type="text"
                      placeholder="Last Name *"
                      value={newContact.last_name}
                      onChange={(e) => setNewContact({...newContact, last_name: e.target.value})}
                      className="px-3 py-2 bg-virgo-dark-700 border border-virgo-teal-500/30 rounded text-white text-sm placeholder-gray-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <input
                      type="email"
                      placeholder="Email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                      className="px-3 py-2 bg-virgo-dark-700 border border-virgo-teal-500/30 rounded text-white text-sm placeholder-gray-500"
                    />
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                      className="px-3 py-2 bg-virgo-dark-700 border border-virgo-teal-500/30 rounded text-white text-sm placeholder-gray-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateContact}
                    disabled={creatingContact}
                    className="w-full py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium disabled:opacity-50"
                  >
                    {creatingContact ? 'Creating...' : 'Create Contact'}
                  </button>
                </div>
              )}
            </div>

            {/* Case Selection */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Case (Optional)
              </label>
              <select
                value={formData.case_id}
                onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                disabled={!formData.contact_id}
                className="w-full px-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500 disabled:opacity-50"
              >
                <option value="">No case selected</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>{caseLabel(c)}</option>
                ))}
              </select>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Due Date *
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
                className="w-full px-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
              />
            </div>

            {/* Payment Terms */}
            <div>
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Payment Terms
              </label>
              <select
                value={formData.payment_terms}
                onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                className="w-full px-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
              >
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
              </select>
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-virgo-teal-300">Line Items</label>
              <button
                type="button"
                onClick={addLineItem}
                className="flex items-center gap-1 text-sm text-virgo-teal-400 hover:text-virgo-teal-300"
              >
                <Plus className="w-4 h-4" /> Add Item
              </button>
            </div>

            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    placeholder="Description"
                    className="flex-1 px-3 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500 text-sm"
                  />
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                    min="0.01"
                    step="0.01"
                    placeholder="Qty"
                    className="w-20 px-3 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500 text-sm"
                  />
                  <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="Price"
                      className="w-full pl-7 pr-3 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500 text-sm"
                    />
                  </div>
                  <div className="w-24 text-right py-2 text-white text-sm">
                    ${(item.quantity * item.unit_price).toFixed(2)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    disabled={lineItems.length === 1}
                    className="p-2 text-gray-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              {/* Tax Rate */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
                />
              </div>

              {/* Discount */}
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Discount Amount ($)
                </label>
                <input
                  type="number"
                  value={formData.discount_amount}
                  onChange={(e) => setFormData({ ...formData, discount_amount: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white focus:outline-none focus:border-virgo-teal-500"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="bg-virgo-dark-800/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Subtotal:</span>
                <span className="text-white">${calculateSubtotal().toFixed(2)}</span>
              </div>
              {formData.tax_rate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Tax ({formData.tax_rate}%):</span>
                  <span className="text-white">${calculateTax().toFixed(2)}</span>
                </div>
              )}
              {formData.discount_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Discount:</span>
                  <span className="text-red-400">-${formData.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-virgo-teal-500/20">
                <span className="text-virgo-teal-300">Total:</span>
                <span className="text-white">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Add any notes for the client..."
              className="w-full px-4 py-2 bg-virgo-dark-800 border border-virgo-teal-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-virgo-teal-500 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-virgo-teal-500/20 bg-virgo-dark-800/30">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-virgo-teal-500 hover:bg-virgo-teal-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateInvoiceModal;
